<template>
  <div :class="bem()">
    <FadeTransition>
      <div
        v-if="props.customisedProduct.conversionKitEnabled && activeSlideIndex < props.staticViewAngles.length"
        :class="bem('ck-toggle-button-desktop')"
      >
        <VisionLink
          line-link
          is-button
          :mono-font="true"
          @click.stop="() => props.customisedProduct.swapFrameType()"
        >
          View {{ props.customisedProduct.alternateFrameTypeDetails?.title }}
        </VisionLink>
      </div>
    </FadeTransition>

    <div
      ref="emblaRef"
      :class="bem('viewport')"
    >
      <div :class="bem('container')">
        <ProductGallerySlides
          :active-slide-index="activeSlideIndex"
          :static-view-angles="props.staticViewAngles"
          :customised-product="props.customisedProduct"
          :custom-image-scaling="customImageScaling"
          :static-product-and-lifestyle-images="staticProductAndLifestyleImages"
          :show360="show360"
          :show-iris-preview="showIrisPreview"
          :view-angle-mapping="viewAngleMapping"
          @launch-fullscreen-product-modal="
            emits('launch-fullscreen-product-modal', $event)
          "
        />
      </div>
    </div>

    <div :class="bem('thumbs')">
      <div :class="bem('thumbs-wrapper')">
        <div :class="bem('thumbs-viewport')">
          <ProductGalleryThumbs
            :active-slide-index="activeSlideIndex"
            :static-view-angles="props.staticViewAngles"
            :customised-product="props.customisedProduct"
            :custom-image-scaling="customImageScaling"
            :static-product-and-lifestyle-images="
              staticProductAndLifestyleImages
            "
            :show360="show360"
            :show-iris-preview="showIrisPreview"
            :view-angle-mapping="viewAngleMapping"
            :show-lens-guide-button="props.showLensGuideButton"
            @click="setActiveSlide"
            @show-lens-guide="emits('show-lens-guide', $event)"
          />
        </div>

        <FadeTransition>
          <div
            v-if="props.customisedProduct.conversionKitEnabled && activeSlideIndex < props.staticViewAngles.length"
            :class="bem('ck-toggle-button-mobile')"
          >
            <VisionLink
              line-link
              is-button
              :mono-font="true"
              @click="() => props.customisedProduct.swapFrameType()"
            >
              View {{ props.customisedProduct.alternateFrameTypeDetails?.title }}
            </VisionLink>
          </div>
        </FadeTransition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomisedProduct from '~/models/CustomisedProduct'
import emblaCarouselVue from 'embla-carousel-vue'

const props = defineProps({
  customisedProduct: {
    type: CustomisedProduct,
    required: true,
  },
  showIrisPreview: {
    type: Boolean,
    default: false,
  },
  staticProductAndLifestyleImages: {
    type: Array,
    default: () => [],
  },
  staticViewAngles: {
    type: Array,
    default: () => [],
  },
  show360: {
    type: Boolean,
    required: true,
  },
  layerTemplate: {
    type: Object,
    default: () => ({}),
  },
  activeLifestyleIndex: {
    type: Number,
    default: 0,
  },
  showLensGuideButton: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits([
  'show-lens-guide',
  'launch-fullscreen-product-modal',
])

const customImageScaling = {
  1: 100,
  2: 120,
  3: 115,
  4: 115,
  5: 115,
  6: 115,
}

const viewAngleMapping = {
  1: 34,
  2: 14,
  3: 58,
  4: 49,
  5: 1,
  6: 46,
}

const bem = useBEM('ProductGallery')

const activeSlideIndex = ref(0)

const [emblaRef, emblaApi] = emblaCarouselVue({
  axis: 'x',
  inViewThreshold: 0.99,
  duration: 20,
  breakpoints: {
    '(min-width: 1000px)': { axis: 'y' },
    '(max-width: 1000px)': { duration: 0 },
  },
})

onMounted(() => {
  if (emblaApi.value)
    emblaApi.value.on('slidesInView', (embla) => {
      if (embla.slidesInView().length) {
        activeSlideIndex.value = embla.slidesInView()[0]
      }
    })
})

function setActiveSlide(index: number) {
  activeSlideIndex.value = index
}

watch(
  () => activeSlideIndex.value,
  (newValue: int) => {
    emblaApi.value?.scrollTo(newValue)
  }
)
</script>

<style lang="postcss" scoped>
.ProductGallery {
  @apply flex flex-col lg:flex-row gap-x-4 mdMax:relative;
}

.ProductGallery__viewport {
  @apply md:overflow-hidden aspect-square
  rounded-lg order-2 w-full h-full relative;
}

.ProductGallery__container {
  @apply flex flex-row lg:flex-col h-full;
}

.ProductGallery__thumbs {
  @screen mdMax {
    @apply left-1/2 -translate-x-1/2 absolute z-10 bottom-4 w-full;
  }

  @screen md {
    @apply mt-4 w-full order-2;
  }

  @screen lg {
    @apply mt-0 max-w-[5rem] w-1/6 order-1;
  }
}

.ProductGallery__thumbs-wrapper {
  @screen mdMax {
    @apply grid grid-cols-3 gap-4 align-middle justify-center;
  }

  @screen md {
    @apply sticky top-4;
  }
}

.ProductGallery__ck-toggle-button-mobile {
  @screen md {
    @apply hidden;
  }
}

.ProductGallery__ck-toggle-button-desktop {
  @screen mdMax {
    @apply hidden;
  }

  @screen lg {
    @apply absolute bottom-4 right-4 z-[1];
  }
}

.ProductGallery__thumbs-viewport {
  @apply smMax:w-auto ml-auto;

  @screen mdMax {
    @apply mx-auto col-start-2 flex items-center h-[18px];
  }
}

.ProductGallery__thumb-nav {
  @apply md:hidden;
}

.ProductGallery__thumb-nav--hide {
  @apply opacity-40;
}

.ProductGallery__thumb-nav--prev {
  @apply ml-auto;
}

.ProductGallery__thumb-nav--next {
  @apply mr-auto;
}
</style>
